import { useState, useCallback } from 'react';
import { useEntitlement } from '../hooks/useEntitlement';
import { createSubscribeHandoffUrl } from '../api/subscribeHandoff';

function todayDismissKey(): string {
  return `hbs:lapsed-member-banner-dismissed-${new Date().toISOString().slice(0, 10)}`;
}

/**
 * Persistent top-of-page banner shown when the user is `lapsed-member` — a
 * member of a family whose backing subscription has lapsed.
 *
 * Two variants, keyed on `ownsUncoveredFamily`. An OWNER whose own coverage
 * lapsed gets "Reactivate" and copy that doesn't tell them to wait for
 * themselves; a member of someone else's lapsed family gets "Subscribe",
 * because a subscription of their own is always purchasable even though it
 * won't restore that family's shared items. The flag is absent on snapshots
 * cached before it shipped, so absent falls back to the member wording — but
 * the CTA is unconditional either way.
 *
 * The transient "your subscription state changed" banner is suppressed for
 * `lapsed-member` (see TransientBanner), so this is the single message for the
 * state. Per-calendar-day dismissal via sessionStorage, matching the other
 * banners.
 */
export function LapsedMemberBanner() {
  const { state, snapshot } = useEntitlement();
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(todayDismissKey()) === '1';
    } catch {
      return false;
    }
  });

  const handleDismiss = useCallback(() => {
    try { sessionStorage.setItem(todayDismissKey(), '1'); } catch { /* ignore */ }
    setDismissed(true);
  }, []);

  // Same one-time handoff token the trial banner mints, so the subscribe app
  // can bootstrap a session instead of rejecting the arrival as unauthorized.
  const handleSubscribe = useCallback(async () => {
    setError(null);
    setOpening(true);
    try {
      window.location.href = await createSubscribeHandoffUrl();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOpening(false);
    }
  }, []);

  if (state !== 'lapsed-member') return null;
  if (dismissed) return null;

  const isOwner = snapshot?.ownsUncoveredFamily === true;

  return (
    <div className="bg-yellow-100 border-b border-yellow-400 px-4 py-2 flex items-center justify-between gap-4">
      <span className="text-sm text-yellow-950">
        {isOwner
          ? <>Your family no longer has an active subscription. Its shared items are unavailable until you renew.{' '}</>
          : <>A family you&apos;re in no longer has an active subscription. Its shared items are unavailable until the family&apos;s owner renews.{' '}</>}
        <button
          type="button"
          data-testid="lapsed-subscribe-cta"
          id="lapsed-member-subscribe-button"
          onClick={handleSubscribe}
          disabled={opening}
          className="text-yellow-950 underline font-medium hover:text-yellow-700 disabled:opacity-60"
        >
          {opening ? 'Opening…' : isOwner ? 'Reactivate' : 'Subscribe'}
        </button>
        {error && <span className="ml-2 text-red-700">{error}</span>}
      </span>
      <button
        id="lapsed-member-banner-dismiss-button"
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="text-yellow-950 hover:text-yellow-700 text-sm"
      >
        ×
      </button>
    </div>
  );
}
